if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariantFromForm() || this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
          this.setOosPopup(this.currentVariant.available);
        }
        this.syncAddButtonState();
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.disableButtons();
        this.setVariantPickerLoading(true);
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );

            if (!variant) {
              this.setUnavailable();
              return;
            }
            this.setVariantPickerLoading(false);
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            this.setVariantPickerLoading(false);
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }

          const variantFromForm = this.getSelectedVariantFromForm();
          const variant = this.getSelectedVariant(html) || variantFromForm;
          const variantAvailable = variant?.available;

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.selectAvailableNextOption();
            this.setUnavailable();
            return;
          }

          this.setOosPopup(variantAvailable);

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          const sourcePrice = html.querySelector('.js-product-price-block');
          if (sourcePrice) {
            const priceBlocks = document.querySelectorAll(
              `.js-product-price-block[data-section-id="${this.sectionId}"]`
            );
            priceBlocks.forEach((block) => {
              block.innerHTML = sourcePrice.innerHTML;
            });
          } else {
            updateSourceFromDestination('price');
          }

          const variantForPrice = variantFromForm || variant;
          const priceBlocks = document.querySelectorAll(
            `.js-product-price-block[data-section-id="${this.sectionId}"]`
          );
          priceBlocks.forEach((priceWrapper) => {
            const wrapper = priceWrapper.closest('.product__price-wrapper');
            if (wrapper) wrapper.classList.remove('product__price-wrapper--loading');
            if (variantForPrice?.id) {
              const priceStr = priceWrapper.getAttribute(`data-variant-${variantForPrice.id}-price`);
              const compareAtStr = priceWrapper.getAttribute(`data-variant-${variantForPrice.id}-compare-at-price`);
              const bundleStr = priceWrapper.getAttribute(`data-variant-${variantForPrice.id}-bundle-value`);
              const priceContainer = priceWrapper.querySelector('.product__price-original');
              const priceValueEl = priceWrapper.querySelector('.product__price-original-value');
              const compareAtEl = priceWrapper.querySelector('[data-product-price-compare-at-value]');
              const bundleEl = priceWrapper.querySelector('[data-product-price-bundle-value]');
              if (priceStr && priceValueEl) priceValueEl.textContent = priceStr;
              if (priceContainer) {
                const compareAtWrap = compareAtEl?.closest('.f-price-item');
                if (compareAtStr && compareAtEl) {
                  priceContainer.classList.add('f-price--on-sale');
                  compareAtEl.textContent = compareAtStr;
                  compareAtWrap?.classList.remove('hidden');
                  priceValueEl?.classList.add('f-price-item--sale');
                } else {
                  priceContainer.classList.remove('f-price--on-sale');
                  compareAtEl && (compareAtEl.textContent = '');
                  compareAtWrap?.classList.add('hidden');
                  priceValueEl?.classList.remove('f-price-item--sale');
                }
              }
              const saveEl = priceWrapper.querySelector('[data-product-price-save]');
              if (saveEl) {
                const saveStr = priceWrapper.getAttribute(`data-variant-${variantForPrice.id}-save`);
                saveEl.textContent = saveStr ? `${saveEl.dataset.saveLabel || 'Save'} ${saveStr}` : '';
                saveEl.classList.toggle('hidden', !saveStr);
              }
              if (bundleEl) {
                if (bundleStr) {
                  bundleEl.textContent = bundleStr;
                  bundleEl.classList.remove('hidden');
                } else {
                  bundleEl.textContent = '';
                  bundleEl.classList.add('hidden');
                }
              }
            }
          });
          // Holiday template: "Save $X" badge over the gallery
          if (variantForPrice?.id) {
            document.querySelectorAll(`#shopify-section-${this.sectionId} [data-media-save-overlay]`).forEach((overlay) => {
              const mediaSaveEl = overlay.querySelector('[data-product-media-save]');
              const amountEl = overlay.querySelector('[data-product-media-save-amount]');
              const soldOutEl = overlay.querySelector('[data-product-media-soldout]');
              const soldOut = overlay.hasAttribute(`data-variant-${variantForPrice.id}-soldout`);
              if (soldOutEl) soldOutEl.classList.toggle('hidden', !soldOut);
              if (!mediaSaveEl || !amountEl) return;
              const saveStr = overlay.getAttribute(`data-variant-${variantForPrice.id}-save`);
              amountEl.textContent = saveStr || '';
              mediaSaveEl.classList.toggle('hidden', !saveStr || soldOut);
            });
          }
          updateSourceFromDestination('ProductInfoAttributes');
          updateSourceFromDestination('Sku');
          updateSourceFromDestination('Inventory');
          updateSourceFromDestination('InventoryCounter');
          updateSourceFromDestination('InventoryCounter-mobile');
          updateSourceFromDestination('Badges');
          updateSourceFromDestination('PricePerItem');
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          updateSourceFromDestination('QuantityRules');
          updateSourceFromDestination('VolumeNote');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);
          if (stickyAtcBar) {
            stickyAtcBar.classList.remove('hidden');
          }

          const newAddButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const isDisabled = !variantAvailable || !newAddButton || newAddButton.hasAttribute('disabled');
          this.currentVariant = variant;
          this.updateButtonsState(isDisabled, {
            updateText: true,
            text: isDisabled ? FoxTheme.variantStrings.soldOut : null,
          });
          this.setVariantPickerLoading(false);

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variant,
              },
            })
          );
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getSelectedVariantFromForm() {
        const scriptId = `ProductVariantData-${this.sectionId}`;
        const scriptEl = this.querySelector(`script#${scriptId}`) || document.getElementById(scriptId);
        if (!scriptEl?.textContent?.trim()) return null;
        let data;
        try {
          data = JSON.parse(scriptEl.textContent.trim());
        } catch {
          return null;
        }
        const variantSelects = this.variantSelectors || this.querySelector('variant-selects');
        if (!variantSelects?.querySelector('.product-form__input')) return null;
        const optionInputs = variantSelects.querySelectorAll('.product-form__input');
        const selectedValues = [];
        optionInputs.forEach((group) => {
          const checked = group.querySelector('input[type="radio"]:checked');
          const select = group.querySelector('select');
          if (checked) selectedValues.push(checked.value || '');
          else if (select?.selectedOptions?.length) selectedValues.push(select.selectedOptions[0].value || '');
          else selectedValues.push('');
        });
        const key = selectedValues.join(',');
        const variantId = data.optionValuesToVariantId?.[key];
        if (variantId == null) return null;
        return data.variants?.find((v) => String(v.id) === String(variantId)) ?? null;
      }

      // getVariantFromCurrentSelection() {
      //   const variantSelects = this.variantSelectors;
      //   if (!variantSelects) return null;
      //   const selectedOptionValues = variantSelects.selectedOptionValues;
      //   if (!selectedOptionValues?.length) return null;
      //   const scriptId = `ProductVariantData-${this.sectionId}`;
      //   const scriptEl = this.querySelector(`script#${scriptId}`) || document.getElementById(scriptId);
      //   if (!scriptEl?.textContent?.trim()) return null;
      //   let data;
      //   try {
      //     data = JSON.parse(scriptEl.textContent.trim());
      //   } catch {
      //     return null;
      //   }
      //   const key = selectedOptionValues.map(String).join(',');
      //   const variantId = data.optionValuesToVariantId?.[key];
      //   if (variantId == null) return null;
      //   const variant = data.variants?.find((v) => String(v.id) === String(variantId));
      //   return variant ?? null;
      // }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            this.setVariantPickerLoading(false);
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      updateKeyIngredientsFromVariant(variant) {
        if (!variant?.id) return;
        const scriptEl = document.getElementById(`ProductInfoAttributesData-${this.dataset.section}`);
        if (!scriptEl?.textContent?.trim()) return;
        let data;
        try {
          data = JSON.parse(scriptEl.textContent.trim());
        } catch {
          return;
        }
        const entry = data[String(variant.id)];
        const span = this.querySelector('.product-categories__value--key-ingredients');
        if (span) {
          span.textContent = entry?.key_ingredients ?? '';
        }
      }

      selectAvailableNextOption() {
        const variantSelects = this.variantSelectors || document.querySelector('variant-selects');
        if (!variantSelects) return;
        const optionGroups = variantSelects.querySelectorAll('.product-form__input');
        const option2 = optionGroups[1];
        if (!option2) return;

        const selectEl = option2.querySelector('select');
        if (selectEl) {
          const firstEnabledOption = option2.querySelector('option:not([disabled])');
          if (firstEnabledOption) {
            selectEl.value = firstEnabledOption.value;
            selectEl.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }

        const firstEnabledRadio = option2.querySelector('input[type="radio"]:not([disabled]):not(.disabled)');
        if (firstEnabledRadio) {
          firstEnabledRadio.checked = true;
          firstEnabledRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      setOosPopup(variantAvailable) {
        const oosPopup = this.querySelector('#oos-popup');
        if (!oosPopup) return;

        oosPopup.classList.toggle('hidden', Boolean(variantAvailable));
      }

      updateButtonsState(disabled, options = {}) {
        const { updateText = false, text = null, updateStyles = true } = options;

        // Handle main add button in product form
        const productForm =
          this.querySelector(`#product-form-${this.dataset.section}`) ||
          document.getElementById(`product-form-${this.dataset.section}`);
        const addButton =
          productForm?.querySelector('[name="add"]') ||
          this.querySelector(`#ProductSubmitButton-${this.sectionId}`);

        if (addButton) {
          const addButtonText = addButton.querySelector('span');

          if (disabled) {
            addButton.setAttribute('disabled', 'disabled');
            if (updateText && text && addButtonText) {
              addButtonText.textContent = this.decoded(text);
            }
          } else {
            const variantId =
              productForm?.querySelector('[name="id"]')?.value ||
              this.querySelector('[name="id"]')?.value;
            const variant = this.currentVariant || this.getSelectedVariantFromForm();
            const isAvailable = variant?.available !== false;

            if (variantId && isAvailable) {
              addButton.removeAttribute('disabled');
              if (updateText && addButtonText) {
                addButtonText.textContent = this.decoded(addButton.dataset.atcText || FoxTheme.variantStrings.addToCart);
              }
            } else {
              addButton.setAttribute('disabled', 'disabled');
              if (updateText && addButtonText) {
                addButtonText.textContent = this.decoded(
                  text || FoxTheme.variantStrings.soldOut
                );
              }
            }
          }

          if (updateStyles) {
            addButton.style.pointerEvents = disabled ? 'none' : '';
            addButton.style.opacity = disabled ? '0.5' : '';
          }
        }

        // Handle sticky ATC button
        const otherButtonSelectors = ['.sticky-atc-bar [name="add"]'];
        otherButtonSelectors.forEach((selector) => {
          const buttons = document.querySelectorAll(selector);
          buttons.forEach((button) => {
            if (disabled) {
              button.setAttribute('disabled', 'disabled');
              if (updateStyles) {
                button.style.pointerEvents = 'none';
                button.style.opacity = '0.6';
              }
            } else {
              button.removeAttribute('disabled');
              if (updateStyles) {
                button.style.pointerEvents = '';
                button.style.opacity = '';
              }
            }
          });
        });
      }

      decoded(text) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        const decoded = tempDiv.textContent;
        return decoded;
      }

      setUnavailable() {
        this.updateButtonsState(true, {
          updateText: true,
          text: FoxTheme.variantStrings.unavailable,
        });
        const priceBlocks = document.querySelectorAll(
          `.js-product-price-block[data-section-id="${this.sectionId}"]`
        );
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const inventoryCounter = document.getElementById(`InventoryCounter-${this.dataset.section}`);
        const inventoryCounterMobile = document.getElementById(`InventoryCounter-mobile-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);
        const volumePricing = document.getElementById(`Volume-${this.dataset.section}`);
        const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);

        priceBlocks.forEach((block) => {
          const wrapper = block.closest('.product__price-wrapper');
          if (wrapper) wrapper.classList.add('product__price-wrapper--loading');
        });
        this.setVariantPickerLoading(false);
        if (inventory) inventory.classList.add('hidden');
        if (inventoryCounter) inventoryCounter.classList.add('hidden');
        if (inventoryCounterMobile) inventoryCounterMobile.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
        if (volumePricing) volumePricing.classList.add('hidden');
        if (stickyAtcBar) stickyAtcBar.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }

      disableButtons() {
        this.updateButtonsState(true);
      }

      enableButtons() {
        this.updateButtonsState(false);
      }

      syncAddButtonState() {
        this.currentVariant =
          this.getSelectedVariantFromForm() || this.getSelectedVariant(this) || this.currentVariant;
        const variant = this.currentVariant;
        const addButton = this.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        const isUnavailable = variant ? !variant.available : addButton?.hasAttribute('disabled');

        if (isUnavailable) {
          this.updateButtonsState(true, {
            updateText: true,
            text: FoxTheme.variantStrings.soldOut,
          });
        }
      }

      setVariantPickerLoading(loading) {
        const variantSelects = this.variantSelectors || this.querySelector('variant-selects');
        if (!variantSelects) return;
        const spinnerEl = variantSelects.querySelector('[data-variant-picker-loading-spinner]');
        if (loading) {
          variantSelects.classList.add('variant-selects--loading');
          if (!spinnerEl) {
            const spinnerHtml = '<div class="variant-selects__loading-spinner loading__spinner" data-variant-picker-loading-spinner aria-hidden="true"><svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle></svg></div>';
            variantSelects.insertAdjacentHTML('beforeend', spinnerHtml);
          }
        } else {
          variantSelects.classList.remove('variant-selects--loading');
          spinnerEl?.remove();
        }
      }
    }
  );
}
